import { useGetApiList, useMutateGetApi } from '@/apiError/apiShow';
import { optionSearchFunction } from '@/utils/common';
import { rules } from '@/utils/validation/FormValidation';
import {
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  BillFormatDataType,
  BranchDataType,
  CompanyDataType,
  FinancialYearDataType,
} from '../common/types';
import { usePostApi } from '@/apiError/apiPostApi';
import BillFormatSkeleton from '../common/skeleton/BillFormatSkeleton';
import { useUpdateApi } from '@/apiError/apiUpdate';
import GlobalModal from '@/utils/modal/Modal';
import { toast } from 'react-toastify';
import { padStart } from 'lodash';

interface propsType {
  open: boolean;
  handleCancel: () => void;
  form: FormInstance<any>;
  referenceId?: string | null;
  setReferenceId?: (id: string | null) => void;
}

const BillFormatModal = ({
  open,
  handleCancel,
  form,
  referenceId,
  setReferenceId,
}: propsType) => {
  const [title, setTitle] = useState('Create Bill Format');
  const [branchList, setBranchList] = useState([]);
  // const [financialYearList, setFinancialYearList] = useState([]);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const billPrefix = Form.useWatch('billPrefix', form);
  const billSuffix = Form.useWatch('billSuffix', form);
  const startNumber = Form.useWatch('startNumber', form);
  const formattedBillNumber = `${billPrefix || ''}${padStart(startNumber, 3, '0') || ''}${billSuffix || ''}`;
  const TRANSACTION_CHOICES = [
    { value: 'purchase', label: 'Purchase' },
    { value: 'sales', label: 'Sales' },
    { value: 'sales_return', label: 'Sales Return' },
    { value: 'purchase_return', label: 'Purchase Return' },
    { value: 'issue_party', label: 'Issue - Party' },
    { value: 'issue_karigar', label: 'Issue - Karigar' },
    { value: 'issue_refine', label: 'Issue - Refine' },
    { value: 'receive_party', label: 'Receive - Party' },
    { value: 'receive_karigar', label: 'Receive - Karigar' },
    { value: 'receive_refine', label: 'Receive - Refine' },
    { value: 'sales_order', label: 'Sales Order' },
    { value: 'metal_cash_payment', label: 'Metal Cash Payment' },
    { value: 'metal_cash_receipt', label: 'Metal Cash Receipt' },
    { value: 'payment', label: 'Payment Voucher' },
    { value: 'receipt', label: 'Receipt Voucher' },
    { value: 'contra', label: 'Contra Voucher' },
    { value: 'journal', label: 'Journal Voucher' },
  ];

  const { data: { data: companyList } = {}, isPending: companyLoading } =
    useGetApiList(`company/access/list/`);

  const { data: { data: storeList } = {}, isPending: storeLoading } =
    useGetApiList(`/admin/stores/`);

  const { mutateAsync: getBranchList, isPending: branchLoading } =
    useMutateGetApi() as ReturnType<typeof useMutateGetApi> & {
      mutateAsync: (vars: { url: string }) => Promise<any>;
    };

  const { mutateAsync: getFinancialYear, isPending: financialYearLoading } =
    useMutateGetApi() as ReturnType<typeof useMutateGetApi> & {
      mutateAsync: (vard: { url: string }) => Promise<any>;
    };

  const { mutateAsync: getEditData, isPending: editDataLoading } =
    useMutateGetApi() as ReturnType<typeof useMutateGetApi> & {
      mutateAsync: (vars: { url: string }) => Promise<any>;
    };

  const { mutateAsync: createBillFormat, isPending: billFormatLoading } =
    usePostApi({ url: `admin/bill-format/` });

  const { mutateAsync: updateData, isPending: updateLoading } =
    useUpdateApi() as ReturnType<typeof useUpdateApi> & {
      mutateAsync: (vars: {
        url: string;
        values: Omit<BillFormatDataType, 'key' | 'index'>;
      }) => Promise<any>;
    };

  const handleCompanyChange = async (value: string) => {
    form.setFieldsValue({ branchId: null });
    const res = await getBranchList({ url: `/companies/${value}/branches/` });
    setBranchList(res?.data);
  };

  // const handleBranchChange = async (value: string) => {
  //   const companyId = form.getFieldValue('companyId');
  //   const branchId = form.getFieldValue('branchId');
  //   const res = await getFinancialYear({
  //     url: `admin/financial-years/by-company-branch/${companyId}/${branchId}/`,
  //   });
  //   setFinancialYearList(res?.data);
  // };

  const handleOk = async () => {
    const fieldsValue = form.getFieldsValue();
    await createBillFormat(fieldsValue);
    setOpenConfirmModal(false);
    handleCancel();
  };

  const handleOnConfirm = async () => {
    // toast.warn('Once the Bill is Created, It cannot be Deleted or Edited?');
    setOpenConfirmModal(true);
  };

  const handleClose = () => {
    setOpenConfirmModal(false);
  };

  const handleUpdate = async () => {
    const fieldsValue = form.getFieldsValue();
    const res = await updateData({
      url: `admin/bill-format/${referenceId}/`,
      values: fieldsValue,
    });

    handleCancel();
    setReferenceId?.(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      if (referenceId) {
        const res = await getEditData({
          url: `admin/bill-format/${referenceId}/`,
        });
        const {
          companyId,
          branchId,
          transactionType,
          billPrefix,
          billSuffix,
          startNumber,
          financialYearId,
          stores,
        } = res?.data;
        const resBranch = await getBranchList({
          url: `/companies/${companyId}/branches/`,
        });
        setBranchList(resBranch?.data);
        const resFinancial = await getFinancialYear({
          url: `admin/financial-years/by-company-branch/${companyId}/${branchId}/`,
        });
        // setFinancialYearList(resFinancial?.data);

        form.setFieldsValue({
          companyId,
          branchId,
          transactionType,
          billPrefix,
          billSuffix,
          startNumber,
          financialYearId,
          storeIds: stores?.map((item: any) => item?.referenceId) ?? [],
        });
        setIsLoading(false);
      }
    };
    referenceId && setTitle('Update Bill Format');
    !referenceId && setTitle('Create Bill Format');
    referenceId && fetchData();
  }, []);
  return (
    <>
      <GlobalModal
        isVisible={openConfirmModal}
        onConfirm={referenceId ? handleUpdate : handleOk}
        onClose={handleClose}
        type={referenceId ? 'update' : 'create'}
        // customContent="Once the Bill is Created , It can not be Deleted or Edited?"
        customContent={`Are you sure you want to ${referenceId ? 'update' : 'create'} the Bill Format?`}
        centered={false}
      />
      <Modal
        forceRender
        destroyOnHidden
        title={
          <div className="tw-text-center tw-my-3 cursor-grab tw-font-semibold tw-text-xl">
            {title
              .split(' ')
              .map((word) => word.split('')[0].toUpperCase() + word.slice(1))
              .join(' ')}
          </div>
        }
        open={open}
        width={1200}
        // height={}
        onOk={() => form.submit()}
        cancelButtonProps={{
          type: 'default',
          style: { backgroundColor: 'red', color: '#fff' },
        }}
        onCancel={handleCancel}
      >
        {isLoading ? (
          <BillFormatSkeleton />
        ) : (
          <Form
            form={form}
            onFinish={handleOnConfirm}
            layout="vertical"
            className="tw-h-48"
            size="large"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  className="font-bold"
                  label="Company"
                  name="companyId"
                  rules={rules.required}
                  validateTrigger="onChange"
                >
                  <Select
                    showSearch
                    loading={companyLoading}
                    placeholder="Select Company"
                    filterOption={optionSearchFunction}
                    onChange={handleCompanyChange}
                    options={companyList?.map((item: CompanyDataType) => {
                      return {
                        value: item.referenceId,
                        label: item?.name,
                      };
                    })}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  className="font-bold"
                  label="Branch"
                  name="branchId"
                  rules={rules.required}
                  validateTrigger="onChange"
                >
                  <Select
                    placeholder="Select Branch"
                    loading={branchLoading}
                    disabled={!branchList?.length}
                    filterOption={optionSearchFunction}
                    // onChange={handleBranchChange}
                    options={branchList?.map((item: BranchDataType) => {
                      return {
                        value: item.referenceId,
                        label: item?.branchName,
                      };
                    })}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  className="font-bold tw-w-full"
                  label="Trasaction Type"
                  name="transactionType"
                  rules={rules.required}
                  validateTrigger="onChange"
                >
                  <Select
                    showSearch
                    placeholder="Select transaction type"
                    filterOption={optionSearchFunction}
                    options={TRANSACTION_CHOICES}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  className="font-bold tw-w-full"
                  label="Store"
                  name="storeIds"
                  validateTrigger="onChange"
                  required
                >
                  <Select
                    showSearch
                    allowClear
                    mode="multiple"
                    loading={storeLoading}
                    placeholder="Select Store"
                    filterOption={optionSearchFunction}
                    options={storeList?.map((item: any) => {
                      return {
                        value: item.referenceId,
                        label: item?.name,
                      };
                    })}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  className="font-bold tw-w-full"
                  label="Bill Prefix"
                  name="billPrefix"
                  rules={rules.required}
                >
                  <Input placeholder="Enter Bill Prefix" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  className="font-bold tw-w-full"
                  label="Start Number"
                  name="startNumber"
                  rules={rules.required}
                >
                  <InputNumber
                    type="number"
                    // min={1}
                    // max={999999999}
                    controls={false}
                    className="tw-w-full"
                    placeholder="Enter Start Number"
                    // maxLength={9}
                    autoComplete="off"
                    // onChange={(value) => {
                    //   if (value !== null) {
                    //     form.setFieldsValue({
                    //       startNumber: padStart(value.toString(), 3, '0'),
                    //     });
                    //   }
                    // }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  className="font-bold tw-w-full"
                  label="Bill Suffix"
                  name="billSuffix"
                  // rules={rules.required}
                  validateTrigger="onChange"
                >
                  <Input placeholder="Entere Bill Suffix" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col span={8} className="tw-flex tw-items-center">
                <div
                  style={{
                    marginTop: '2px',
                    // marginBottom: '2px',
                    fontWeight: '600',
                    fontStyle: 'italic',
                    color: 'green',
                    fontSize: '16px',
                  }}
                  className="tw-bg-green-50 tw-border tw-border-green-200 tw-w-full"
                >
                  Bill Number Preview : {formattedBillNumber}
                </div>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                {/* <Form.Item
                  className="font-bold tw-w-full"
                  label="Financial Year"
                  name="financialYearId"
                  rules={rules.required}
                  validateTrigger="onChange"
                >
                  <Select
                    placeholder="Select a financial Year"
                    disabled={!financialYearList?.length}
                    options={financialYearList?.map(
                      (item: FinancialYearDataType) => {
                        return {
                          value: item?.referenceId,
                          label: (
                            <span>
                              <span>{item?.startDate}</span>{' '}
                              <span
                                style={{ margin: '0 6px', fontSize: '12px' }}
                              >
                                →
                              </span>{' '}
                              <span>{item?.endDate}</span>
                            </span>
                          ),
                        };
                      },
                    )}
                  />
                </Form.Item> */}
              </Col>
            </Row>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default BillFormatModal;
